import { inject, Injectable, Injector } from '@angular/core';
import { NavigationBehaviorOptions, NavigationExtras, Router, UrlSerializer, UrlTree } from '@angular/router';
import { RouteService, NavigationInfo } from './route.service';
import { cloneDeep, isArray, isString, omit, pick } from 'lodash';



@Injectable({
  providedIn: 'root'
})
export class FooRouterService extends Router  {


  protected _urlSerializer: UrlSerializer = inject(UrlSerializer);
  protected _injector: Injector = inject(Injector);

  override navigate(commands: any[], extras?: NavigationExtras): Promise<boolean> {

    const routeService = this._injector.get(RouteService);

    const baseUrl = commands.length ? commands.join('/') : '';

    if (routeService.disableDynamicRouting) {
      return super.navigate(baseUrl ? [extras?.relativeTo ? commands : this.checkForParentRoute(baseUrl, routeService, extras?.info?.['relativeRoute'])] : [], {
        ...this.calculateOptions(extras?.info, extras?.queryParams || {}, routeService),
        queryParamsHandling: extras?.queryParamsHandling,
        ...(extras?.queryParamsHandling ? {
          queryParamsHandling: extras.queryParamsHandling
        } : {}),
        replaceUrl: extras?.replaceUrl,
        ...(extras?.relativeTo ? {
          relativeTo: extras.relativeTo
        } : {}),
      });
    }

    const providedParams = this.convertQueryParamsToQueryString(extras?.queryParams || {});
    const url = (commands.length ? baseUrl : routeService.routesHistory.join('/')) + (providedParams ? ('?' + providedParams) : '');
    const { navigationUrl, queryParams } = routeService.getRoute(url, pick(extras?.info || {}, ['absoluteRoute', 'relativeRoute']));
    return super.navigate([navigationUrl.split('?')[0]], {
      ...this.calculateOptions(extras?.info, queryParams, routeService),
      queryParamsHandling: extras?.queryParamsHandling,
      replaceUrl: extras?.replaceUrl
    });
  }

  override navigateByUrl(url: string | UrlTree, extras?: NavigationBehaviorOptions): Promise<boolean> {
    url = decodeURIComponent(isString(url) ? url : this._urlSerializer.serialize(url));
    const routeService = this._injector.get(RouteService);
    const baseUrl = url.split('?')[0];
    const params = url.split('?')[1] || '';
    if (routeService.disableDynamicRouting) {
      return super.navigateByUrl(this.checkForParentRoute(baseUrl, routeService, extras?.info?.['relativeRoute']) + (params ? ('?' + params) : ''), {
        ...this.calculateOptions(extras?.info, routeService.getQueryParamsFromQueryString(params), routeService),
        replaceUrl: extras?.replaceUrl
      });
    }

     const { navigationUrl, queryParams } = routeService.getRoute(url, pick(extras?.info || {}, ['absoluteRoute', 'relativeRoute']));
     return super.navigateByUrl(navigationUrl, {
      ...this.calculateOptions(extras?.info, queryParams, routeService),
      replaceUrl: extras?.replaceUrl
    });
  } 




  calculateOptions(info: NavigationInfo, queryParams: any, routeService: RouteService): any {
    let stateKeys: any = info?.stateKeys || [];
    if (stateKeys && !isArray(stateKeys)) {
      stateKeys = [stateKeys];
    } 

   
    (stateKeys || []).forEach((stateKey, idx) => {
      if (!routeService.preservedStateKeys.includes(stateKey)) {
        if (!idx) {
          routeService.states[stateKey] = {...routeService.states[stateKey],  ...queryParams, ...(info.stateData || {})};
        } else {
          routeService.states[stateKey] = {...routeService.states[stateKey],  ...queryParams};
        }
      }
    });
   
    let newState = cloneDeep(routeService.states);

    const routerOptions: any = {
      queryParams: queryParams,
      state: stateKeys?.length ?  pick(newState, stateKeys.concat(routeService.preservedStateKeys)) : pick(newState, routeService.preservedStateKeys)
    };

    if (info) {
      routerOptions.info = info;
    } 

    if (info?.mergeQueryParams) {
      routerOptions.queryParamsHandling = 'merge';
    }
    return routerOptions;
  } 
 


  

  convertQueryParamsToQueryString(queryParams: { [key: string]: any }): string {
    const params: string[] = [];
  
    Object.entries(queryParams).forEach(([key, value]) => {
      if (value == null) return;
  
      const encodedKey = encodeURIComponent(key);
  
      if (Array.isArray(value)) {
        value.forEach(v => {
          if (v != null) {
            params.push(`${encodedKey}=${encodeURIComponent(v)}`);
          }
        });
      } else {
        params.push(`${encodedKey}=${encodeURIComponent(value)}`);
      }
    });
  
    return params.join('&');
  }

  checkForParentRoute(route: string, routeService: RouteService, relativeToThisRoute: boolean) {
    let finalRoute = route.startsWith('/') ? route.substring(1) : route;
    if (relativeToThisRoute) {
      const currentUrl = this.url;
      const cleanUrl = currentUrl?.split(/[?#]/)[0] || '';
      return `${cleanUrl}/${finalRoute}`;
    }
  
    const currentUrlSegments = this.url.split('/');
    const finalRouteFirstSegment = finalRoute.split('/')[0];
  
    for (const parentRoute of routeService.parentRoutes) {
      const hasValidPath = currentUrlSegments.length > 1;
      const containsParentRoute = currentUrlSegments.includes(parentRoute);
      const shouldPrependParent = finalRouteFirstSegment !== parentRoute;
  
      if (hasValidPath && containsParentRoute && shouldPrependParent) {
        return `${currentUrlSegments[1]}/${finalRoute}`;
      }
    }
    if (window['prefix'] && finalRoute.indexOf(window['prefix']) === -1) {
      finalRoute = '/' + `${window['prefix']}` + (!finalRoute.startsWith('/') ? '/' : '') + finalRoute;
    }
    return finalRoute;
  }
}
